import gsap from 'gsap';

// Components
import Modal from './modal';
import Header from '../global/header/header';

const settings = {
    videoIdAttribute: 'data-video-id',
    tertiaryVideoIdAttribute: 'data-for-video-id',
    videoHashPattern: /^#video-(?<videoId>[0-9a-z]+)$/i,

    classNames: {
        blockModalAnimation: 'video-modal--no-animation',
        copyNotification: 'video-modal--share-bar--notification',
    },

    selectors: {
        videoWrapper: '.video-modal--video',
        shareBar: '.video-modal--share-bar',
        videoButtons: 'button[data-video-id]',
    },

    dataAttributes: {
        videoId: 'data-video-id',
    },

    templates: {
        modal: `<dialog class="modal video-modal">
            <div class="modal__container">
                <button class="modal__close-button"></button>
                <div class="modal__content">
                    <div class="video-modal--video"></div>

                    <div class="video-modal--share-bar">
                        <div>
                            <div>
                                <span>share</span>
                                <span>video link copied</span>
                            </div>
                            <div>
                                <button></button>
                                <button></button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </dialog>`,

        iframeAutoplay: `<iframe
            src="https://player.vimeo.com/video/%% ID %%?api=false&amp;autopause=true&amp;autoplay=true&amp;byline=false&amp;loop=false&amp;portrait=false&amp;title=false&amp;xhtml=false"
            frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
        ></iframe>`,

        iframePaused: `<iframe
            src="https://player.vimeo.com/video/%% ID %%?api=false&amp;autopause=true&amp;autoplay=false&amp;byline=false&amp;loop=false&amp;portrait=false&amp;title=false&amp;xhtml=false"
            frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
        ></iframe>`,
    },

    shareEmail: {
        subject: 'Watch this video from Axogen',
        body: 'I found this video from Axogen and thought you might find it interesting: %% URL %%',
        urlPattern: '%% URL %%',
    },

    notificationTimings: {
        transition: 0.5,
        pause: 0.5,
    },
};

// Check if a hash matching our video ID pattern is present
let queryVideoId;
if (window.location.hash) {
    const matches = settings.videoHashPattern.exec(window.location.hash);
    if (matches) {
        // The hash in the URL matches our expected pattern
        queryVideoId = matches.groups.videoId;
    }
}

export default class VideoModal extends Modal {
    constructor(
        videoId,
        triggerElement = null,
    ) {
        if (!videoId) {
            throw new Error();
        }

        videoId = videoId.toString();

        // Remove any "?" query from the video ID, if present (e.g. "?h=", which is for unlisted videos)
        const videoIdQueryIndex = videoId.indexOf('?');
        if (videoIdQueryIndex > 0) {
            videoId = videoId.substring(0, videoIdQueryIndex);
        }

        // Call base class
        super(settings.templates.modal);

        this.videoId = videoId;
        this.autoplayEnabled = null;
        this.videoWrapper = this.container
            .querySelector(settings.selectors.videoWrapper);

        // Prepare the direct link URL for this video
        this.videoUrl = new URL(window.location.href);
        this.videoUrl.hash = `video-${this.videoId}`;
        this.videoUrl.search = '';
        this.videoUrl = this.videoUrl.toString();

        // Setup share bar
        this.shareBar = new ShareBar(
            this.container.querySelector(settings.selectors.shareBar),
            this.videoUrl,
        );

        // If a video ID was detected in the URL, check if it matches our ID
        if (this.videoId == queryVideoId) {
            // If an associated trigger element was specified, scroll to it
            if (triggerElement) {
                const bounds = triggerElement.getBoundingClientRect();
                window.scrollTo({
                    left: 0,
                    top: (bounds.top + window.pageYOffset) - Header.height,
                    behavior: 'instant',
                });
            }

            // Temporarily disable the show/hide CSS transition on video modals
            document.body.classList
                .add(settings.classNames.blockModalAnimation);

            // Show our modal
            this.show(true, false);

            // Re-enable modal CSS transitions after a brief delay
            setTimeout(
                () => {
                    document.body.classList
                        .remove(settings.classNames.blockModalAnimation);
                },
                500,
            );
        }
    }

    static setupVideoPlayButtons() {
        const videoButtons = document
            .querySelectorAll(settings.selectors.videoButtons);
        const foundTertiaryVideoButtons = [];

        for (const videoButton of videoButtons) {
            // Setup video button
            const videoId = videoButton
                .getAttribute(settings.dataAttributes.videoId);
            if (!videoId) {
                continue;
            }

            const modal = new VideoModal(
                videoId,
                videoButton,
            );

            videoButton.addEventListener('click', (event) => {
                event.preventDefault();

                modal.show();
            });

            // Setup any tertiary buttons referencing this video
            const tertiaryVideoButtons = document.querySelectorAll(`*[${settings.tertiaryVideoIdAttribute} = "${videoId}"]`);
            for (const tertiaryVideoButton of tertiaryVideoButtons) {
                // Skip any buttons that have already been assigned to a previous video
                if (foundTertiaryVideoButtons.includes(tertiaryVideoButton)) {
                    continue;
                }

                // Add this button to our found button array
                foundTertiaryVideoButtons.push(tertiaryVideoButton);

                // Add click event
                tertiaryVideoButton.addEventListener('click', (event) => {
                    event.preventDefault();

                    modal.show();
                });
            }
        }
    }

    show(
        immediate = false,
        autoplay = true,
    ) {
        // Check whether the current URL matches the direct link for this video
        let urlMatchesThisVideo = false;
        const matches = settings.videoHashPattern.exec(window.location.hash);
        if (matches) {
            if (matches.groups.videoId == this.videoId) {
                urlMatchesThisVideo = true;
            }
        }

        if (!urlMatchesThisVideo) {
            // The current URL doesn't match the direct link for this video -
            // push a new state to adjust it
            window.history.pushState(
                {
                    videoId: this.videoId,
                },
                null,
                this.videoUrl,
            );
        }

        // Check if there's been a change to our autoplay settings
        if (this.autoplayEnabled !== autoplay) {
            this.autoplayEnabled = autoplay === true;

            // Recreate iframe
            let iframe = this.videoWrapper.querySelector('iframe');
            if (iframe) {
                iframe.remove();
            }

            iframe = document.createElement('div');
            iframe.innerHTML = (
                this.autoplayEnabled ?
                    settings.templates.iframeAutoplay :
                    settings.templates.iframePaused
            ).replace('%% ID %%', this.videoId);

            iframe = iframe.firstChild;
            this.videoWrapper.appendChild(iframe);
        }

        // Call the base method
        super.show(immediate);
    }

    close(immediate = false) {
        // Check if the current window history state still reflects our
        // hash modification
        let urlAdjusted = false;
        if (window.history.state) {
            if (window.history.state.videoId == this.videoId) {
                // Undo our pushed state
                urlAdjusted = true;
                window.history.back();
            }
        }

        if (!urlAdjusted) {
            if (settings.videoHashPattern.exec(window.location.hash)) {
                // We didn't detect a pushed state, but the current
                // hash looks to be a video ID - replace it
                window.history.pushState(null, null, window.location.pathname);
            }
        }

        // Call the base method
        super.close(immediate);
    }
}

class ShareBar {
    constructor(
        container,
        videoUrl,
    ) {
        // Set up share buttons and copy notification
        const shareMailButton = container.querySelector('button:nth-of-type(1)');
        const shareCopyButton = container.querySelector('button:nth-of-type(2)');
        const buttonsContainer = container.querySelector(':scope > div > div:nth-of-type(1)');
        const shareLabel = container.querySelector('span:nth-of-type(1)');
        const linkCopiedLabel = container.querySelector('span:nth-of-type(2)');
        let notificationTimeline = null;

        shareMailButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const subject = encodeURIComponent(settings.shareEmail.subject);

            const body = encodeURIComponent(
                settings.shareEmail.body.replace(
                    settings.shareEmail.urlPattern,
                    videoUrl,
                ),
            );

            window.open(`mailto:?subject=${subject}&body=${body}`);
        });

        shareCopyButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            // Copy the URL to the clipboard
            navigator.clipboard
                .writeText(videoUrl)
                .then(() => {
                    // Show notification
                    if (notificationTimeline) {
                        notificationTimeline.progress(1);
                    }

                    notificationTimeline = gsap.timeline();

                    const initialWidth = buttonsContainer.clientWidth;
                    container.classList
                        .add(settings.classNames.copyNotification);

                    let startTime = 0;
                    notificationTimeline.from(
                        buttonsContainer,
                        {
                            duration: settings.notificationTimings.transition,
                            width: initialWidth,
                            ease: 'power2.inOut',
                        },
                        startTime,
                    );

                    notificationTimeline.from(
                        shareLabel,
                        {
                            duration: settings.notificationTimings.transition,
                            opacity: 1,
                            ease: 'power2.inOut',
                        },
                        startTime,
                    );

                    notificationTimeline.from(
                        linkCopiedLabel,
                        {
                            duration: settings.notificationTimings.transition,
                            opacity: 0,
                            ease: 'power2.inOut',
                        },
                        startTime,
                    );

                    startTime = settings.notificationTimings.transition +
                        settings.notificationTimings.pause;
                    notificationTimeline.to(
                        buttonsContainer,
                        {
                            duration: settings.notificationTimings.transition,
                            width: initialWidth,
                            ease: 'power2.inOut',
                        },
                        startTime,
                    );

                    notificationTimeline.to(
                        shareLabel,
                        {
                            duration: settings.notificationTimings.transition,
                            opacity: 1,
                            ease: 'power2.inOut',
                        },
                        startTime,
                    );

                    notificationTimeline.to(
                        linkCopiedLabel,
                        {
                            duration: settings.notificationTimings.transition,
                            opacity: 0,
                            ease: 'power2.inOut',
                        },
                        startTime,
                    );

                    notificationTimeline.add(() => {
                        // Clean up
                        container.classList
                            .remove(settings.classNames.copyNotification);

                        buttonsContainer.removeAttribute('style');
                        shareLabel.removeAttribute('style');
                        linkCopiedLabel.removeAttribute('style');

                        notificationTimeline = null;
                    });
                })
                .catch((error) => {
                    console.log({
                        message: 'copy failed',
                        error: error,
                    });
                });
        });
    }
}
